import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { Exact } from "@Helpers/types/exact";

/**
 * App-level user-profile modal state. Plain-DOM grid cells (userTagDom.ts)
 * can't host React modals, so a click dispatches here and a single
 * UserProfileModalHost mounted in App.tsx renders the modal.
 */
export interface UserProfileModalState {
  /** User whose profile modal is open, or null when closed */
  userId: string | null;
  /** Ids shown in the grouped-users details modal, or null when closed */
  groupIds: string[] | null;
  /** Header for the grouped-users details modal */
  groupHeader?: string;
}

interface OpenGroupedUsersPayload {
  ids: string[];
  header?: string;
}

const initialState: UserProfileModalState = {
  userId: null,
  groupIds: null,
  groupHeader: undefined,
};

const userProfileModalSlice = createSlice({
  name: "userProfileModal",
  initialState,
  reducers: {
    openUserProfile: (state: UserProfileModalState, action: PayloadAction<string>) => {
      state.userId = action.payload;
    },
    openGroupedUsersModal: (state: UserProfileModalState, action: PayloadAction<OpenGroupedUsersPayload>) => {
      state.groupIds = action.payload.ids;
      state.groupHeader = action.payload.header;
    },
    closeUserProfileModals: (state: UserProfileModalState) => {
      state.userId = null;
      state.groupIds = null;
      state.groupHeader = undefined;
    },
  },
});

const { openGroupedUsersModal: _openGroupedUsersModalRaw } = userProfileModalSlice.actions;

export const { openUserProfile, closeUserProfileModals } = userProfileModalSlice.actions;

/** Object payload wrapped with `Exact<>` so excess properties are caught (see userTagData.ts). */
export const openGroupedUsersModal = <T extends Exact<OpenGroupedUsersPayload, T>>(payload: T) =>
  _openGroupedUsersModalRaw(payload);

export default userProfileModalSlice.reducer;
